import { useTranslation } from "react-i18next";
import { IDashboardStats } from "../../models/DashboardStats";
import DashboardTile from "./DashboardTile";

interface IMaterialsRecoveredTableProps {
  dashboardStats: IDashboardStats | undefined;
  span?: 1 | 2;
}

type DashboardStatsExtended = IDashboardStats & {
  steelToday?: number;
  steelTodayTarget?: number;
  aluminiumToday?: number;
  aluminiumTodayTarget?: number;
  copperToday?: number;
  copperTodayTarget?: number;
  plasticToday?: number;
  plasticTodayTarget?: number;
  leadBatteriesToday?: number;
  leadBatteriesTodayTarget?: number;
  catalyticConvertersToday?: number;
  catalyticConvertersTodayTarget?: number;
  glassToday?: number;
  glassTodayTarget?: number;
  motorsToday?: number;
  motorsTodayTarget?: number;
  alloyWheelsToday?: number;
  alloyWheelsTodayTarget?: number;
};

type RowConfig = {
  valueKey: keyof DashboardStatsExtended;
  targetKey: keyof DashboardStatsExtended;
  label: string;
  bgClass: string;
  textClass: string;
};

const rowConfig: RowConfig[] = [
  { valueKey: "steelToday", targetKey: "steelTodayTarget", label: "Steel", bgClass: "bg-slate-50", textClass: "text-slate-700" },
  { valueKey: "aluminiumToday", targetKey: "aluminiumTodayTarget", label: "Aluminium", bgClass: "bg-gray-50", textClass: "text-gray-700" },
  { valueKey: "copperToday", targetKey: "copperTodayTarget", label: "Copper", bgClass: "bg-amber-50", textClass: "text-amber-700" },
  { valueKey: "plasticToday", targetKey: "plasticTodayTarget", label: "Plastic", bgClass: "bg-sky-50", textClass: "text-sky-700" },
  { valueKey: "leadBatteriesToday", targetKey: "leadBatteriesTodayTarget", label: "Lead Batteries", bgClass: "bg-indigo-50", textClass: "text-indigo-700" },
  { valueKey: "catalyticConvertersToday", targetKey: "catalyticConvertersTodayTarget", label: "Catalytic Converters", bgClass: "bg-rose-50", textClass: "text-rose-700" },
  { valueKey: "glassToday", targetKey: "glassTodayTarget", label: "Glass", bgClass: "bg-cyan-50", textClass: "text-cyan-700" },
  { valueKey: "motorsToday", targetKey: "motorsTodayTarget", label: "Motors", bgClass: "bg-emerald-50", textClass: "text-emerald-700" },
  { valueKey: "alloyWheelsToday", targetKey: "alloyWheelsTodayTarget", label: "Alloy Wheels", bgClass: "bg-violet-50", textClass: "text-violet-700" },
];

function formatValue(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return String(value);
}

export default function MaterialsRecoveredTable({ dashboardStats, span = 2 }: IMaterialsRecoveredTableProps) {
  const { t } = useTranslation("dashboard");

  if (!dashboardStats) return null;

  const stats = dashboardStats as DashboardStatsExtended;

  return (
    <DashboardTile span={span}>
      <div className="p-3">
        <div className="mb-3 w-full">
          <h3 className="prose font-semibold text-gray-900">{t("Materials Recovered")}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{t("Current count for the day vs target")}</p>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800">{t("Material")}</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Today")}</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Target")}</th>
              </tr>
            </thead>
            <tbody>
              {rowConfig.map(({ valueKey, targetKey, label, bgClass, textClass }) => {
                const value = stats[valueKey] as number | null | undefined;
                const target = stats[targetKey] as number | null | undefined;
                return (
                  <tr key={String(valueKey)} className={`font-medium transition-colors ${bgClass}`}>
                    <td className={`py-2.5 pl-4 pr-4 ${textClass}`}>{t(label)}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${textClass}`}>{formatValue(value)}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${textClass}`}>{formatValue(target)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardTile>
  );
}
